import {
    faChevronsLeft,
    faChevronsRight,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";
import { DateRangeField } from "../../../ui/Inputs";
import { MenuCategory } from "../../MapV2/ui/Menu";
import { isValidDate } from "./state";

interface DatePickerProps {
    after?: Date;
    onChangeAfter: (d: Date) => void;
    before?: Date;
    onChangeBefore: (d: Date) => void;
}

export const DatePicker = (props: DatePickerProps) => {
    // Range shifting is only possible when both dates are set.
    const rangeComplete = !!props.after && !!props.before;

    const monthsBetweenDates = useMemo(() => {
        if (!rangeComplete) {
            return 0;
        }
        const d1 = new Date(props.after).getTime();
        const d2 = new Date(props.before).getTime();

        const isInvalid = d1 > d2;
        if (isInvalid) {
            return 0;
        }

        const difference = Math.abs(d2 - d1);
        const months = difference / (1000 * 60 * 60 * 24 * 30);

        return Math.round(months);
    }, [props.before, props.after, rangeComplete]);

    const updateRange = (months: number) => {
        props.onChangeAfter(
            new Date(new Date().setMonth(new Date().getMonth() - months)),
        );
        props.onChangeBefore(new Date());
    };

    const moveNPeriods = (periodsToMove: number) => {
        if (!rangeComplete) {
            return;
        }
        const d1 = new Date(props.after).getTime();
        const d2 = new Date(props.before).getTime();
        const difference = Math.abs(d2 - d1);
        props.onChangeAfter(new Date(d1 + periodsToMove * difference));
        props.onChangeBefore(new Date(d2 + periodsToMove * difference));
    };

    return (
        <div className="bg-white px-1 py-0.5 border rounded-md text-sm w-[400px]">
            <div className="flex w-full items-center justify-between">
                <MenuCategory>Filter by date range</MenuCategory>
                <div className="flex items-center gap-2">
                    {[
                        ["3m", 3],
                        ["6m", 6],
                        ["1y", 12],
                        ["3y", 12 * 3],
                        ["5y", 12 * 5],
                    ].map((r: [string, number]) => (
                        <button
                            key={r[0]}
                            onClick={() => updateRange(r[1])}
                            type="button"
                            className={`
                                flex items-center gap-1 text-ae-blue-550 text-xs
                                hover:bg-ae-blue-50 p-1 rounded
                                ${monthsBetweenDates === r[1] ? "bg-ae-blue-50" : ""}
                            `}
                        >
                            {r[0]}
                        </button>
                    ))}
                    <button
                        type="button"
                        disabled={!rangeComplete}
                        onClick={() => moveNPeriods(-1)}
                        className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                        <FontAwesomeIcon icon={faChevronsLeft} />
                    </button>
                    <button
                        type="button"
                        disabled={!rangeComplete}
                        onClick={() => moveNPeriods(1)}
                        className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                        <FontAwesomeIcon icon={faChevronsRight} />
                    </button>
                </div>
            </div>
            <DateRangeField
                valueAfter={
                    isValidDate(props.after)
                        ? props.after.toISOString()
                        : undefined
                }
                valueBefore={
                    isValidDate(props.before)
                        ? props.before.toISOString()
                        : undefined
                }
                onChangeAfter={(v) => {
                    const d = new Date(v);
                    if (isValidDate(d)) props.onChangeAfter(d);
                }}
                onChangeBefore={(v) => {
                    const d = new Date(v);
                    if (isValidDate(d)) props.onChangeBefore(d);
                }}
                showPresets={false}
                showRangeNavigation={false}
            />
        </div>
    );
};
